import type { Schemas } from "@norn/sdk"
import { Link } from "@tanstack/react-router"
import { ArrowRight } from "lucide-react"
import { type ReactNode, useMemo } from "react"
import { ErrorState } from "@/components/states"
import { Skeleton } from "@/components/ui/skeleton"
import type { Scope } from "@/lib/connection"
import { useLocale, useT } from "@/lib/i18n"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"
import { useLiveObjects, useOntology, useReferenceIndex, useTitleOf } from "@/workspace/hooks"
import { bucketsOf, drillTypes, groupCandidates, type MetricTile, metricTiles } from "@/workspace/metrics"
import { type Ontology, optionLabel } from "@/workspace/ontology"
import { HiddenChip, isMasked, numericText, RefusalNote, RefusalSummary, useFormatters } from "@/workspace/values"
import { DataFrame } from "./frame"

type Obj = Schemas["ObjectList"]["items"][number]

export function MetricsPage() {
  const t = useT()
  const scope = useScope()
  const { preference } = useLocale()
  const { ontology, defs } = useOntology(scope)
  const tiles = useMemo(() => (defs.data && ontology ? metricTiles(defs.data, ontology, preference.locale) : []), [defs.data, ontology, preference.locale])
  return (
    <DataFrame title={t("dv.nav.metrics")} description={t("dv.metrics.description")} how={["dv.how.metrics"]} crumbs={[t("dv.nav.metrics")]}>
      {defs.isError ? (
        <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
      ) : !ontology ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : tiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("dv.metrics.none")}</p>
      ) : (
        <div className="space-y-6">
          {tiles.map((tile) => (
            <MetricBlock key={tile.id} scope={scope} tile={tile} ontology={ontology} />
          ))}
        </div>
      )}
    </DataFrame>
  )
}

const current = (o: Obj) => (o.values as Record<string, unknown>).status === "current"

function MetricBlock({ scope, tile, ontology }: { scope: Scope; tile: MetricTile; ontology: Ontology }) {
  const t = useT()
  const fmt = useFormatters()
  // Same live read as Home: re-read while any readable row is not yet current.
  const list = useLiveObjects(scope, tile.objectType, 20, (data) =>
    data?.items.some((o) => !isMasked(o.masked as Record<string, unknown>, "status") && !current(o)) ? 10_000 : 60_000,
  )
  const rows = tile.rowKey ? (list.data?.items ?? []).filter((r) => r.key === tile.rowKey) : (list.data?.items ?? [])
  const snapshot = [...ontology.types.values()].find((ty) => ["buckets", "definition", "freshness"].every((f) => ty.fields.some((x) => x.id === f)))
  const drill = drillTypes(tile, ontology)
  return (
    <section className="rounded-2xl border border-border bg-card" aria-label={tile.title}>
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-5 py-4">
        <h2 className="text-[15px] font-semibold tracking-tight">{tile.title}</h2>
        {tile.caption ? <p className="text-sm text-muted-foreground">{tile.caption}</p> : null}
        <span className="ms-auto flex flex-wrap gap-2 text-xs">
          {drill.map((type) => (
            <Link key={type} to="/$orgId/$environmentId/data/explore" params={scope} search={{ type }} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 hover:bg-muted/60">
              {t("dv.metrics.drill", { name: ontology.types.get(type)?.pluralLabel ?? type })} <ArrowRight className="size-3 rtl:-scale-x-100" aria-hidden />
            </Link>
          ))}
        </span>
      </header>
      {tile.help ? <p className="px-5 pt-3 text-xs text-muted-foreground">{tile.help}</p> : null}
      {list.isPending ? (
        <Skeleton className="m-5 h-16" />
      ) : list.isError ? (
        <RefusalNote error={list.error} title={t("ws.home.metricUnavailable")} className="m-5" />
      ) : (
        <div className="overflow-x-auto px-2 pb-2">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="text-[11px] text-muted-foreground uppercase tracking-[0.06em]">
              <tr>
                {[t("dv.metrics.row"), t("dv.metrics.value"), t("ws.lineage.freshness"), t("ws.lineage.sourceHead"), t("ws.lineage.freshUntil"), t("ws.lineage.definition"), t("ws.lineage.result")].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-start font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <MetricRow key={row.key} scope={scope} row={row} ontology={ontology} tile={tile} snapshotType={snapshot?.name} fmt={fmt} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function MetricRow({
  scope,
  row,
  ontology,
  tile,
  snapshotType,
  fmt,
}: {
  scope: Scope
  row: Obj
  ontology: Ontology
  tile: MetricTile
  snapshotType?: string
  fmt: ReturnType<typeof useFormatters>
}) {
  const t = useT()
  const v = row.values as Record<string, unknown>
  const masked = row.masked as Record<string, unknown> | undefined
  const hidden = (id: string) => isMasked(masked, id)
  const buckets = hidden("buckets") ? [] : bucketsOf(v.buckets).filter((b) => b.group !== null)
  const value = numericText(v.value)
  const status = typeof v.status === "string" && !hidden("status") ? v.status : null
  const resultKey = typeof v.result_key === "string" && !hidden("result_key") ? v.result_key : null
  const cell = (id: string, render: () => ReactNode) => (hidden(id) ? <HiddenChip className="h-5 text-2xs" /> : render())
  return (
    <>
      <tr className="border-t border-border/70 align-top">
        <td className="px-3 py-2.5">
          <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: row.objectType, key: row.key }} className="hover:underline">
            {row.key}
          </Link>
        </td>
        <td className="px-3 py-2.5 font-medium tabular-nums">{cell("value", () => (value !== null ? fmt.number(value) : buckets.length ? t("dv.metrics.groups", { count: buckets.length }) : "—"))}</td>
        <td className="px-3 py-2.5">
          {status ? (
            <span className="inline-flex items-center gap-1.5 text-xs">
              <span aria-hidden className={cn("size-1.5 rounded-full", status === "current" ? "bg-[var(--success)]" : "bg-[var(--warning)]")} />
              {optionLabel(status)}
            </span>
          ) : (
            cell("status", () => "—")
          )}
        </td>
        <td className="px-3 py-2.5 tabular-nums">{cell("accepted_head", () => (typeof v.accepted_head === "number" ? fmt.number(v.accepted_head) : "—"))}</td>
        <td className="px-3 py-2.5 text-xs">{cell("fresh_until", () => (typeof v.fresh_until === "number" ? fmt.date(new Date(v.fresh_until).toISOString()) : "—"))}</td>
        <td dir="ltr" className="px-3 py-2.5 font-mono text-2xs text-muted-foreground">
          {cell("definition", () => (typeof v.definition === "string" ? `${v.definition.slice(0, 12)}…` : "—"))}
        </td>
        <td dir="ltr" className="px-3 py-2.5 font-mono text-2xs">
          {resultKey && snapshotType ? (
            <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: snapshotType, key: resultKey }} className="text-muted-foreground hover:text-foreground hover:underline">
              {resultKey.slice(0, 19)}…
            </Link>
          ) : (
            cell("result_key", () => "—")
          )}
        </td>
      </tr>
      {buckets.length > 1 ? (
        <tr>
          <td colSpan={7} className="px-3 pb-3">
            <Buckets scope={scope} tile={tile} ontology={ontology} buckets={buckets} fmt={fmt} />
          </td>
        </tr>
      ) : null}
    </>
  )
}

/** Bucket groups, resolved to their records' names when the group is a readable record key. */
function Buckets({
  scope,
  tile,
  ontology,
  buckets,
  fmt,
}: {
  scope: Scope
  tile: MetricTile
  ontology: Ontology
  buckets: ReturnType<typeof bucketsOf>
  fmt: ReturnType<typeof useFormatters>
}) {
  const t = useT()
  const candidates = groupCandidates(tile, ontology)
  const shown = buckets.slice(0, 12)
  const refs = shown.flatMap((b) => candidates.map((objectType) => ({ objectType, key: b.group as string })))
  const index = useReferenceIndex(scope, refs)
  const titleOf = useTitleOf(ontology)
  const max = Math.max(1, ...shown.map((b) => b.value))
  const failed = refs.map((r) => index(r.objectType, r.key)).filter((r) => r.isError).map((r) => r.error)
  return (
    <>
    <RefusalSummary errors={failed} title={t("ws.related.unreadable")} className="mb-2" />
    <ul className="grid gap-x-8 gap-y-1.5 rounded-xl bg-muted/30 px-4 py-3 sm:grid-cols-2">
      {shown.map((b) => {
        const group = b.group as string
        const found = candidates.map((c) => ({ c, o: index(c, group).data })).find((x) => x.o)
        const title = found?.o ? titleOf(found.o, group) : null
        return (
          <li key={group} className="grid grid-cols-[minmax(0,1fr)_6rem_auto] items-center gap-3 text-xs">
            {found?.o ? (
              <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: found.c, key: group }} className="truncate hover:underline" title={group}>
                {title && !title.isKey ? `${title.title} · ${group}` : group}
              </Link>
            ) : (
              <span className="truncate text-muted-foreground">{optionLabel(group)}</span>
            )}
            <span aria-hidden className="h-1.5 rounded-full bg-foreground/15">
              <span className="block h-full rounded-full bg-foreground/60" style={{ width: `${(b.value / max) * 100}%` }} />
            </span>
            <span className="tabular-nums">{fmt.number(b.text)}</span>
          </li>
        )
      })}
    </ul>
    </>
  )
}
